/**
 * 核心需要的外部能力。只声明，不实现，src-core 里没有任何 Tauri、Vue、Node 的 import。
 * 二期 Android 换实现，这些接口不动。
 * 任何层都可以依赖它，它反过来只引用 model 与 review 的类型。
 * 这里只有契约与两个纯函数（seededRng 与 parseSettings），不做 IO，不写日志。
 */

import type { Issue } from './model/result'
import type { DeviceId, EpochDay, RepoPath } from './model/ids'
import type { Review } from './review/scheduling'

/** 知识仓库里的一个 Markdown 文件。线上传输形状，parsing 是唯一消费者。 */
export interface RawFile {
	readonly path: RepoPath
	readonly text: string
}

/** git 同步结果。Rust 侧的 stderr 与退出码在这一层翻译成枚举，业务逻辑看不到 git。 */
export type SyncReport =
	| { readonly status: 'up-to-date' }
	| { readonly status: 'updated'; readonly commitsBehind: number }
	| { readonly status: 'cloned' }
	| { readonly status: 'failed'; readonly reason: SyncFailure }

export type SyncFailure = 'offline' | 'not-a-repo' | 'dirty-worktree' | 'auth' | 'timeout' | 'other'

export interface FilesPort {
	/** 只返回 concepts 目录下的 md 文件。路径过滤在这一层，核心拿到的是已过滤结果。 */
	readConceptFiles(): Promise<readonly RawFile[]>
}

/**
 * 日志整读的产物。撕裂的末行与坏 JSON 在实现侧丢弃并回报，
 * 因为「哪一行坏了」是磁盘事实，核心无法自己判断。
 */
export interface LogRead {
	readonly reviews: readonly Review[]
	readonly issues: readonly Issue[]
}

export interface ReviewLogPort {
	readAll(): Promise<LogRead>
	/** 追加一行并以换行结尾落盘。单写者，无锁。 */
	append(review: Review): Promise<void>
}

export interface Clock {
	/** 本地日历日。进程生命周期内可以跨天，所以每次重算队列都重新取。 */
	today(): EpochDay
	nowMs(): number
}

export interface Rng {
	/** [0, bound)。测试注入固定种子实现，所以抽卡可以断言。 */
	nextInt(bound: number): number
}

/**
 * 可复现的随机源：同一 seed 给出同一序列。
 * sample intent 带 seed 就走这里，所以「昨天那批抽到的知识点」能被重放出来。
 */
export function seededRng(seed: number): Rng {
	let state = seed >>> 0
	return {
		// mulberry32 的一步 32 位混合。纯算术、无全局状态，所以同一 seed 在桌面和 Android 上给出同一条序列。
		// bound <= 0 返回 0：调用方只在池子非空时抽，这里不为空池设计第二条出口。
		nextInt(bound: number): number {
			state = (state + 0x6d2b79f5) >>> 0
			let t = Math.imul(state ^ (state >>> 15), 1 | state)
			t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
			return Math.floor((((t ^ (t >>> 14)) >>> 0) / 0x100000000) * bound)
		},
	}
}

/** 可配部分只有新卡数量。device 由平台侧首次启动生成，核心不编造标识。 */
export interface Settings {
	readonly newPerDay: number
}

export const DEFAULT_SETTINGS: Settings = { newPerDay: 10 }

/** 设置文件是不透明键值，核心只在自己这一侧解释它，越界与非数字键被忽略并回报。 */
export type RawSettings = Readonly<Record<string, unknown>>

export interface SettingsPort {
	read(): Promise<RawSettings>
	write(settings: Settings): Promise<void>
}

export function parseSettings(raw: RawSettings): { readonly settings: Settings; readonly ignored: readonly string[] } {
	const ignored: string[] = []
	let newPerDay = DEFAULT_SETTINGS.newPerDay
	for (const [key, value] of Object.entries(raw)) {
		// 只认这一个键。认识但值不合法，和不认识的键，走同一条 ignored 通道，诊断口径只有一套。
		if (key === 'newPerDay' && typeof value === 'number' && Number.isInteger(value) && value >= 0) newPerDay = value
		else ignored.push(key)
	}
	return { settings: { newPerDay }, ignored }
}

export interface Ports {
	readonly files: FilesPort
	readonly reviews: ReviewLogPort
	readonly clock: Clock
	readonly rng: Rng
	readonly settings: SettingsPort
	/** 本设备标识，二期多端合并日志时区分写入者。 */
	device(): Promise<DeviceId>
	pullRepo(): Promise<SyncReport>
}
